import { useQuery } from 'react-query';

import { getV2ApiCompanyIdSpendLimitStatus } from 'src/client/spendesk-api';
import { type QueryState } from 'src/core/api/queryState';
import { mapQueryResultToQueryState } from 'src/core/api/utils/queryState';

import { useCompany } from '../../app/hooks/useCompany';
import { type SpendLimitStatus } from '../models/spendLimit';

export const useSpendLimitStatusQuery = ({
  requestDate,
  expenseCategoryId,
  enabled,
}: {
  requestDate: Date;
  expenseCategoryId: string;
  enabled: boolean;
}): QueryState<SpendLimitStatus> => {
  const company = useCompany();
  const requestDateString = formatDate(requestDate);
  const result = useQuery(
    ['spend-limits', 'status', expenseCategoryId, requestDateString],
    async (): Promise<SpendLimitStatus> => {
      const { body, statusCode } = await getV2ApiCompanyIdSpendLimitStatus({
        path: { companyId: company.id },
        query: { expenseCategoryId, date: requestDateString },
      });
      if (statusCode !== 200) {
        throw new Error('Failed to fetch spend limit status');
      }

      const { spendLimitStatus, spendLimit } = body;
      return {
        spendLimitStatus: Object.values(spendLimitStatus),
        spendLimit,
      };
    },
    {
      enabled,
      retry: false,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
  return mapQueryResultToQueryState(result);
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
